import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Footer from '../../Footer';
import { POSTS } from './blogData';
import './BlogPost.css';

const CATEGORY_COLORS = {
    'Classes': '#c8a97e',
    'Origins': '#8fbc8f',
    'Coffee Education': '#87CEEB',
};

function renderBlock(block, i) {
    switch (block.type) {
        case 'heading':
            return <h2 key={i} className='bp-heading'>{block.text}</h2>;

        case 'paragraph':
            return <p key={i} className='bp-paragraph'>{block.text}</p>;

        case 'callout':
            return (
                <div key={i} className='bp-callout'>
                    <span className='bp-callout-label'>{block.label}</span>
                    <p className='bp-callout-text'>{block.text}</p>
                </div>
            );

        case 'stat-grid':
            return (
                <div key={i} className='bp-stat-block'>
                    <p className='bp-block-label'>{block.label}</p>
                    <div className='bp-stat-grid'>
                        {block.stats.map((s, j) => (
                            <div key={j} className='bp-stat-card'>
                                <span className='bp-stat-value'>{s.value}</span>
                                <span className='bp-stat-label'>{s.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case 'elevation-bar':
            return (
                <div key={i} className='bp-elevation-block'>
                    <p className='bp-block-label'>{block.label}</p>
                    <div className='bp-elevation-rows'>
                        {block.bars.map((b, j) => (
                            <div key={j} className='bp-elevation-row'>
                                <span className='bp-elevation-name'>{b.label}</span>
                                <div className='bp-elevation-compare'>
                                    <div className='bp-elevation-side bp-elevation-low'>
                                        <span className='bp-elevation-tag'>LOW ALTITUDE</span>
                                        <span className='bp-elevation-val'>{b.low}</span>
                                    </div>
                                    <div className='bp-elevation-arrow'>→</div>
                                    <div className='bp-elevation-side bp-elevation-high'>
                                        <span className='bp-elevation-tag'>HIGH ALTITUDE</span>
                                        <span className='bp-elevation-val'>{b.high}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case 'region-cards':
            return (
                <div key={i} className='bp-regions'>
                    {block.regions.map((r, j) => (
                        <div key={j} className={`bp-region-card${r.highlight ? ' bp-region-card--highlight' : ''}`}>
                            <div className='bp-region-header'>
                                <h3 className='bp-region-name'>{r.name}</h3>
                                <div className='bp-region-meta'>
                                    <span className='bp-region-badge'>⛰ {r.elevation}</span>
                                    <span className='bp-region-badge'>🌋 {r.soil}</span>
                                </div>
                            </div>
                            <p className='bp-region-notes'>{r.notes}</p>
                            {r.highlight && <span className='bp-region-in-shop'>★ Available in our shop</span>}
                        </div>
                    ))}
                </div>
            );

        default:
            return null;
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const post = POSTS.find((p) => p.slug === slug);

    useEffect(() => {
        if (!post) return;
        document.title = `${post.title} | OFFF THE ROAST`;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': post.title,
            'description': post.excerpt,
            'datePublished': post.date,
            'author': { '@type': 'Organization', 'name': 'OFFF THE ROAST' },
            'publisher': {
                '@type': 'Organization',
                'name': 'OFFF THE ROAST',
                'url': 'https://www.offftheroast.com'
            },
            'url': `https://www.offftheroast.com/blog/${post.slug}`,
            'image': `https://www.offftheroast.com${post.image}`
        };
        let tag = document.getElementById('blog-post-schema');
        if (!tag) {
            tag = document.createElement('script');
            tag.id = 'blog-post-schema';
            tag.type = 'application/ld+json';
            document.head.appendChild(tag);
        }
        tag.textContent = JSON.stringify(schema);

        return () => {
            const t = document.getElementById('blog-post-schema');
            if (t) t.remove();
        };
    }, [post]);

    if (!post) {
        return (
            <>
                <div className='bp-page'>
                    <div className='bp-not-found'>
                        <h1>Post not found</h1>
                        <Link to='/blog' className='bp-back'>← Back to Blog</Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const currentIndex = POSTS.findIndex((p) => p.slug === slug);
    const prevPost = POSTS[currentIndex + 1] || null;
    const nextPost = POSTS[currentIndex - 1] || null;

    return (
        <>
            <div className='bp-page'>
                <div className='bp-container'>
                    <Link to='/blog' className='bp-back'>← Back to Blog</Link>

                    <div className='bp-header'>
                        <span
                            className='bp-category'
                            style={{ color: CATEGORY_COLORS[post.category] || '#c8a97e', borderColor: CATEGORY_COLORS[post.category] || '#c8a97e' }}
                        >
                            {post.category}
                        </span>
                        <h1 className='bp-title'>{post.title}</h1>
                        <p className='bp-date'>{post.date}</p>
                    </div>

                    <div className='bp-divider' />

                    <div className='bp-content'>
                        {post.content.map((block, i) => renderBlock(block, i))}
                    </div>

                    <div className='bp-divider' />

                    <div className='bp-nav'>
                        {prevPost ? (
                            <button className='bp-nav-btn' onClick={() => navigate(`/blog/${prevPost.slug}`)}>
                                <span className='bp-nav-label'>← Older</span>
                                <span className='bp-nav-title'>{prevPost.title}</span>
                            </button>
                        ) : <div />}
                        {nextPost ? (
                            <button className='bp-nav-btn bp-nav-btn--right' onClick={() => navigate(`/blog/${nextPost.slug}`)}>
                                <span className='bp-nav-label'>Newer →</span>
                                <span className='bp-nav-title'>{nextPost.title}</span>
                            </button>
                        ) : <div />}
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
